import getConfig from 'next/config'
import { EntityTypeEnum } from '@fe/common/api/models/enums/EntityTypeEnum'
import { FTN } from '@fe/common/constants/brands'

/**
 * FINTWIN brand discriminator.
 *
 * The brand is fixed at build time (one image per brand) and surfaced via
 * `publicRuntimeConfig.brandName` (env `BRAND_NAME`). This mirrors the
 * established theme-branching precedent in `themeUtils.getTheme()`, which
 * switches on the same brand constants.
 *
 * Not a Redux `(state) => ...` selector: brandName never changes at runtime,
 * so this is a plain function safe to call in components, SSR
 * (`getServerSideProps`) and at module top-level. `getConfig()` returns
 * undefined outside the Next runtime, hence the guard.
 *
 * Deliberately keyed off `brandName`, NOT `config.company_name`: the prior
 * fintwin attempt used that free-text field, which is fragile and was rejected.
 */
export const isFintwin = (): boolean =>
  getConfig()?.publicRuntimeConfig?.brandName === FTN

/**
 * Decides whether the legacy individual verification page should hand off to
 * the FINTWIN onboarding flow.
 *
 * Pure on purpose: the brand result (from `isFintwin()`) and the entity type
 * are passed in, so the decision is trivially unit-testable and the legacy
 * `getServerSideProps` stays a thin caller. The handoff is gated on BOTH the
 * FINTWIN brand AND an Individual entity - Legal entities keep the corporate
 * flow, and the other 11 brands (where `isFintwin()` is false) are a no-op.
 */
export const shouldRedirectToFintwin = (
  isFintwinResult: boolean,
  entityType: EntityTypeEnum | undefined
): boolean => isFintwinResult && entityType === EntityTypeEnum.Individual
